import { AppBar, Box, Button, Container, Toolbar, Typography, IconButton } from '@material-ui/core'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import lendxLogo from '../../assets/portalLogo.svg'
import { getUserEntitiesNJourneys, triggerInvite, triggerPortalInvite, getUserInfo } from '../../services/bff-service'
import { setEntities } from '../../stores/entity/entity-action'
import { useEntityState } from '../../stores/entity/entity.hooks'
import { setSnackBar } from '../../stores/ui/ui-action'
import { UserState } from '../../stores/user/user-state'
import { useUser } from '../../stores/user/user.hooks'
import { saveUserInfoAction } from '../../stores/user/user-action'
import { fetchCodatConnectInformationByCompanyId } from '../../stores/async/async-actions'

import FENERGO_CONSTANTS from '../../utils/fenergo-constants'
import CONSTANTS from '../../utils/constants'
import { getValidJourney, transformMultipleJourneysIntoSingle } from '../../utils/fenergo-utils'
import { PortalLoading } from '../portal-loading/portal-loading'
import PortalLogout from '../portal-logout/portal-logout'
import usePortalStyles from './portal.styles'
import usePoll from '../../shared/hooks/use-polling'
import { useCodatState } from '../../stores/ui/ui.hooks'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

export const Portal = () => {
    const classes = usePortalStyles()
    const user = useUser()
    const dispatch = useDispatch()
    const codatState = useCodatState()
    const companies = user.actions?.companies.length > 0 ? [user.actions?.companies?.[0]] : []
    const [company, setCompany] = useState<any>()

    const [loading, setLoading] = useState(false)
    const navigate = useNavigate()
    const { result, start: startPoll, stop: stopPoll } = usePoll(getUserEntitiesNJourneys, 5000)
    const { result: userResult, start: startUserPoll, stop: stopUserPoll } = usePoll(getUserInfo, 5000)
    const noDlaExist = useMemo(() => company && company?.dlas?.length === 0, [company])

    useEffect(() => {
        if (!company) setCompany(companies?.[0])
    }, [companies])

    const fetchCodatPlatform = (company) => {
        if (company?.dba_name) {
            const adfs = company.adfs ?? []
            const dlas = company.dlas ?? []
            let codatCompanyId
            if (adfs.length > 0) {
                codatCompanyId = adfs[0]?.codat_company_id
            } else if (dlas.length > 0) {
                codatCompanyId = dlas[0]?.codat_company_id
            }
            if (codatCompanyId) {
                dispatch(fetchCodatConnectInformationByCompanyId(codatCompanyId))
            }
        }
    }
    useEffect(() => {
        fetchCodatPlatform(company)
    }, [company])
    const identifyCompany = (companies) => {
        return companies?.find((c) => c.dba_name === company?.dba_name)
    }
    useEffect(() => {
        if (isEmpty(result) || isEmpty(userResult)) return
        const entityWrappedInArray = transformMultipleJourneysIntoSingle(result)

        if (entityWrappedInArray.length === 0) return

        const companyData = identifyCompany(userResult.actions?.companies)

        if (isInProgressJourney(entityWrappedInArray) && isInProgressDla(companyData ?? {})) {
            dispatch(setEntities(entityWrappedInArray))
            dispatch(saveUserInfoAction(userResult))
            stopPoll()
            stopUserPoll()
            setLoading(false)
            navigate('/journey')
            navigate(0)
        }
    }, [result, userResult])

    useEffect(() => {
        if (isEmpty(userResult)) return
        const companyData = identifyCompany(userResult.actions?.companies)
        if (isInProgressAdf(companyData ?? {})) {
            dispatch(saveUserInfoAction(userResult))
            stopUserPoll()
            setLoading(false)
            navigate('/connect/sync-your-accounting')
            navigate(0)
        }
    }, [userResult])

    async function handleLoanRenewal() {
        const companyId = company?.company_id
        const dbaName = company?.dba_name
        await triggerInvite({ ...user, companyName: dbaName, companyId })
        setLoading(true)
        startPoll()
        startUserPoll()

        // Alert user if creating a new journey takes too long.
        setTimeout(() => {
            stopPoll()
            stopUserPoll()
            setLoading(false)
            dispatch(setSnackBar({ show: true, message: 'We encountered an issue. Please try again.', messageType: 'error' }))
        }, 90_000)
    }
    const reApplyHandler = () => {
        if (company?.dba_name) {
            handleLoanRenewal()
        } else {
            navigate('/in-usa?is_next_company=true')
        }
    }
    async function handleAdfConnect() {
        const companyId = company?.company_id
        const dbaName = company?.dba_name
        await triggerPortalInvite({ ...user, companyName: dbaName, companyId })
        setLoading(true)
        startUserPoll()

        // Alert user if creating a new adf takes too long.
        setTimeout(() => {
            stopUserPoll()
            setLoading(false)
            dispatch(setSnackBar({ show: true, message: 'We encountered an issue. Please try again.', messageType: 'error' }))
        }, 90_000)
    }
    const connectAdf = () => {
        if (company?.dba_name) {
            handleAdfConnect()
        }
    }
    return (
        <Container maxWidth={false} style={{ background: '#fff', display: 'flex', flexDirection: 'column', padding: 0 }}>
            {/* <div className={classes.rootBg}></div> */}
            <div className={classes.root}>
                <AppBar className={classes.appbar}>
                    <Toolbar className={classes.toolbar}>
                        <img src={lendxLogo} alt="Lendx Logo" className={classes.logoImage} />

                        <div className={classes.rightSection}>
                            <div className={classes.bannerSection}>
                                <Typography className={classes.welcomeSection}>Welcome, {(user as UserState)?.firstName}</Typography>
                                <Typography className={classes.emailSection}>({(user as UserState)?.email})</Typography>
                            </div>
                            <div className={classes.verticalRow}></div>
                            <PortalLogout />
                        </div>
                    </Toolbar>
                </AppBar>
            </div>
            <div className={classes.rootBg}>
                <Box className={classes.mainContainer}>
                    <div className={classes.container}>
                        <div className={classes.thankYouBox}>
                            <Typography className={classes.thankYouBoxTitle}>Welcome back, {(user as UserState)?.firstName}.</Typography>
                            <Typography className={classes.thankYouBoxDesc}>
                                Manage your existing loan applications, start a new application for a different company, or connect your
                                accounting software to First Citizens Bank.
                            </Typography>
                        </div>
                        <div className={classes.companyContainer}>
                            <div className={classes.companyHeaderContainer}>
                                <Typography className={classes.companyHeaderTitle}>Your Companies</Typography>
                                <Typography className={classes.companyHeaderDescription}>
                                    Select the company you’re here for today:
                                </Typography>
                            </div>

                            <div className={classes.companyFramesContainer}>
                                {companies &&
                                    companies?.map((c: any) => {
                                        return (
                                            <div
                                                key={c.dba_name}
                                                onClick={() => setCompany(c)}
                                                className={`${classes.existingCompanyFrame} ${c.dba_name === company?.dba_name && classes.existingCompanyFrameSelected}`}
                                            >
                                                <div className={classes.existingCompany}>
                                                    <div className={classes.existingCompanyIcon}></div>
                                                </div>
                                                <Typography className={classes.companyTitle}>{c.dba_name}</Typography>
                                            </div>
                                        )
                                    })}
                                <div
                                    onClick={() => {
                                        setCompany({})
                                    }}
                                    className={`${classes.companyFrame} ${!company?.dba_name && classes.companyFrameSelected}`}
                                >
                                    <div className={classes.newCompany}>
                                        <div
                                            className={`${classes.newCompanyIcon} ${!company?.dba_name && classes.newCompanyIconSelected}`}
                                        ></div>
                                    </div>
                                    <Typography
                                        className={`${classes.newCompanyTitle}  ${!company?.dba_name && classes.newCompanyTitleSelected}`}
                                    >
                                        New Company
                                    </Typography>
                                </div>
                            </div>

                            <div className={classes.actionContainer}>
                                <Typography className={classes.actionTitle}>Actions</Typography>
                                <div className={classes.actionContentContainer}>
                                    {company?.dba_name && !noDlaExist && (
                                        <div className={classes.loanContainer}>
                                            <div className={classes.loanTopContainer}>
                                                <div className={classes.loanIconContainer}>
                                                    <div className={classes.loanIcon}></div>
                                                </div>
                                                <div className={classes.loanContentContainer}>
                                                    <Typography className={classes.loanTitle}>Reapply for a Loan</Typography>
                                                    <Typography className={classes.loanDescription}>
                                                        Ready to apply again? Review your details and submit a new application.
                                                    </Typography>
                                                </div>
                                            </div>
                                            <div className={classes.loanButton} onClick={() => reApplyHandler()}>
                                                <div className={classes.loanButtonText}>Review & Reapply</div>
                                                <div className={classes.loanButtonNextIcon}></div>
                                            </div>
                                        </div>
                                    )}
                                    {(!company?.dba_name || noDlaExist) && (
                                        <div className={classes.loanContainer}>
                                            <div className={classes.loanTopContainer}>
                                                <div className={classes.loanIconContainer}>
                                                    <div className={classes.loanIcon}></div>
                                                </div>
                                                <div className={classes.loanContentContainer}>
                                                    <Typography className={classes.loanTitle}>Apply for a Loan</Typography>
                                                    <Typography className={classes.loanDescription}>
                                                        Start a new application for a different company.
                                                    </Typography>
                                                </div>
                                            </div>
                                            <div className={classes.loanStartButton} onClick={() => reApplyHandler()}>
                                                <div className={classes.loanStartButtonText}>Start</div>
                                                <div className={classes.loanStartButtonNextIcon}></div>
                                            </div>
                                        </div>
                                    )}
                                    {company?.dba_name && (
                                        <div className={classes.adfContainer}>
                                            <div className={classes.adfTopContainer}>
                                                <div className={classes.adfIconContainer}>
                                                    <div className={classes.adfIcon}></div>
                                                </div>
                                                <div className={classes.adfContentContainer}>
                                                    <Typography className={classes.adfTitle}>Connect Accounting Software</Typography>
                                                    <Typography className={classes.adfDescription}>
                                                        Link your accounting software to automate monthly reporting.
                                                    </Typography>
                                                </div>
                                            </div>
                                            {codatState.platformName ? (
                                                <div className={classes.adfConnectedaButton}>
                                                    <CheckCircleIcon style={{ color: 'green' }} />
                                                    <div className={classes.adfConnectedButtonText}>
                                                        Connected:{codatState.platformName}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className={classes.adfButton} onClick={connectAdf}>
                                                    <div className={classes.adfButtonText}>Connect</div>
                                                    <div className={classes.adfButtonNextIcon}></div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </Box>
            </div>
            {loading && (
                <PortalLoading>
                    Starting New Application (this may take a minute) <b>Please do not refresh the page</b> ...
                </PortalLoading>
            )}
        </Container>
    )
}

// Helpers

function isInProgressJourney(entityWrappedInArray: any): boolean {
    return getValidJourney(entityWrappedInArray[0]?.company?.journeys)?.journeyStatus === FENERGO_CONSTANTS.JOURNEY_STATUSES.IN_PROGRESS
}
function isInProgressAdf(company: any): boolean {
    return company?.adfs?.some((a) => a.status !== CONSTANTS.ADF_STATUSES.COMPLETED)
}
function isInProgressDla(company: any): boolean {
    return company?.dlas.find((dla) => {
        return !dla.dla_end_datetime
    })
}
function isEmpty(result: any): boolean {
    return result === undefined || result === null || Object.keys(result).length === 0
}
